/** Keys resolved by the active locale. Copy lives in src/i18n/<locale>. */
export const dataErrorKey = {
  network: "crm.errors.network",
  forbidden: "crm.errors.forbidden",
  server: "crm.errors.server",
  generic: "crm.errors.generic",
} as const;

type ErrorFields = {
  name?: unknown;
  message?: unknown;
  status?: unknown;
  code?: unknown;
  body?: { code?: unknown; status?: unknown } | null;
};

const asFields = (error: unknown): ErrorFields | undefined => {
  if (typeof error !== "object" || error === null) return undefined;
  return error as ErrorFields;
};

const readText = (error: unknown): string => {
  if (typeof error === "string") return error;
  const message = asFields(error)?.message;
  return typeof message === "string" ? message : "";
};

const readStatus = (error: unknown): number | undefined => {
  const fields = asFields(error);
  if (!fields) return undefined;
  const candidates = [fields.status, fields.body?.status];
  for (const candidate of candidates) {
    if (typeof candidate === "number" && Number.isFinite(candidate)) {
      return candidate;
    }
    if (typeof candidate === "string" && candidate.trim() !== "") {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return undefined;
};

const readCode = (error: unknown): string | undefined => {
  const fields = asFields(error);
  if (!fields) return undefined;
  if (typeof fields.code === "string") return fields.code;
  if (typeof fields.body?.code === "string") return fields.body.code;
  return undefined;
};

const isNetworkError = (error: unknown): boolean => {
  const fields = asFields(error);
  const name = typeof fields?.name === "string" ? fields.name : "";
  const message = readText(error);
  if (name === "NetworkError") return true;
  if (name === "TypeError" && /failed to fetch|networkerror/i.test(message)) {
    return true;
  }
  return /failed to fetch|networkerror/i.test(message);
};

const UNREACHABLE_STATUSES = new Set([0, 502, 503, 504]);

/**
 * A failed load (offline, DNS, gateway) rather than a real 404.
 * Also matches errors whose message was already rewritten to the network key.
 */
export const isUnreachableError = (error: unknown): boolean => {
  if (isNetworkError(error)) return true;
  if (readText(error) === dataErrorKey.network) return true;
  const status = readStatus(error);
  return status != null && UNREACHABLE_STATUSES.has(status);
};

/** Maps a data-layer failure to a translation key for every save, create, and delete. */
export const dataErrorMessageKey = (error: unknown): string => {
  if (isNetworkError(error)) return dataErrorKey.network;
  const code = readCode(error);
  const status = readStatus(error);
  if (code === "42501" || status === 401 || status === 403) {
    return dataErrorKey.forbidden;
  }
  if (typeof status === "number" && status >= 500 && status < 600) {
    return dataErrorKey.server;
  }
  return dataErrorKey.generic;
};

/** Keeps status and body, and replaces the message with a key the notification layer translates. */
export const toDataError = <T>(error: T): T => {
  const message = dataErrorMessageKey(error);
  if (typeof error === "object" && error !== null) {
    try {
      (error as { message?: string }).message = message;
      return error;
    } catch {
      return { ...error, message } as T;
    }
  }
  return new Error(message) as T;
};

export const withDataErrorKeys = <T extends object>(provider: T): T => {
  const wrapped = { ...provider } as T;
  for (const key of Object.keys(provider) as (keyof T)[]) {
    const value = provider[key];
    if (typeof value !== "function") continue;
    (wrapped as Record<string, unknown>)[key as string] = (
      ...args: unknown[]
    ) => {
      try {
        const result = value.apply(provider, args);
        if (
          result &&
          typeof (result as { then?: unknown }).then === "function"
        ) {
          return (result as Promise<unknown>).catch((error: unknown) => {
            throw toDataError(error);
          });
        }
        return result;
      } catch (error) {
        throw toDataError(error);
      }
    };
  }
  return wrapped;
};
